import React, { useState, useEffect } from "react";
import {
  OMNISCAN_CORPORATE_RED,
  OMNISCAN_CORPORATE_GREEN,
  OMNISCAN_CORPORATE_TURQUOIS,
  OMNISCAN_CORPORATE_PINK,
  OMNISCAN_CORPORATE_PURPLE
} from "../../../constants/colors";

import AssetChartOutages from "./AssetChartOutages";
import AssetChartAvailability from "./AssetChartAvailability";

/* ======================================================
   MAIN COMPONENT
====================================================== */

export default function AssetDetailDrawerContent(props) {

    const title = props.title || "Filtered Asset Detail";
    const subtitle = props.subtitle || "High / Fair (example)";

    /* =========================
       COLOURS
    ========================== */

    function adjustColor(hex, amount) {
        let color = hex.replace("#", "");

        let r = parseInt(color.substring(0, 2), 16);
        let g = parseInt(color.substring(2, 4), 16);
        let b = parseInt(color.substring(4, 6), 16);

        r = Math.min(255, Math.max(0, r + amount));
        g = Math.min(255, Math.max(0, g + amount));
        b = Math.min(255, Math.max(0, b + amount));

        return (
            "#" +
            r.toString(16).padStart(2, "0") +
            g.toString(16).padStart(2, "0") +
            b.toString(16).padStart(2, "0")
        );
    }

    const OMNI = {
        TURQUOIS: OMNISCAN_CORPORATE_TURQUOIS,
        GREEN: OMNISCAN_CORPORATE_GREEN,
        RED: OMNISCAN_CORPORATE_RED,
        PINK: OMNISCAN_CORPORATE_PINK,
        PURPLE: OMNISCAN_CORPORATE_PURPLE
    };

   

	const kpis = props.kpis || {
	    uptimePercent: 97.4,
	    downtimeHours: 1120,
	    outages: 14
	};

    function formatPercent(v) {
        return v === null || v === undefined ? "-" : v.toFixed(1) + "%";
    }

    /* =========================
       STYLES
    ========================== */

    const container = {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "18px 18px 18px 22px",
        boxSizing: "border-box",
        width: "calc(100% - 30px)"
    };

   

    const kpiRow = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" };
    const kpiCardBase = {
        borderRadius: "14px",
        padding: "10px 14px",
        minHeight: "60px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        color: "#fff",
        boxShadow: "0 10px 22px rgba(0,0,0,0.14)"
    };

    const kpiColours = [
        "linear-gradient(135deg," + adjustColor(OMNI.TURQUOIS, -20) + "," + adjustColor(OMNI.TURQUOIS, 25) + ")",
        "linear-gradient(135deg," + adjustColor(OMNI.GREEN, -20) + "," + adjustColor(OMNI.GREEN, 25) + ")",
        "linear-gradient(135deg," + adjustColor(OMNI.PURPLE, -25) + "," + adjustColor(OMNI.PURPLE, 30) + ")",
        "linear-gradient(135deg," + adjustColor(OMNI.PINK, -20) + "," + adjustColor(OMNI.PINK, 25) + ")"
    ];

    const kpiLabel = { fontSize: "10px", letterSpacing: "0.8px", textTransform: "uppercase", opacity: 0.9 };
    const kpiValue = { fontSize: "20px", fontWeight: 800 };
    const kpiSub = { fontSize: "11px", opacity: 0.9 };

    const gridTwoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" };
    const sectionCard = { borderRadius: "16px", background: "#fff", boxShadow: "0 14px 28px rgba(0,0,0,0.10)", padding: "14px" };
    const sectionTitle = { fontSize: "11px", letterSpacing: "0.8px", textTransform: "uppercase", fontWeight: 800, color: "#444", marginBottom: "8px" };
    const placeholderChart = { height: "150px", borderRadius: "14px", background: "linear-gradient(180deg,#f5f6f8,#eceef2)", border: "1px solid rgba(0,0,0,0.06)" };

    const rightPanel = { borderRadius: "16px", background: "rgba(245,246,248,0.95)", border: "1px solid rgba(0,0,0,0.06)", padding: "12px", minHeight: "330px" };
    const searchBox = { height: "34px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.15)", padding: "0 12px", fontSize: "12px" };
    const list = { display: "flex", flexDirection: "column", gap: "8px", overflowY: "auto" };
    const assetTop = { display: "flex", justifyContent: "space-between", marginBottom: "4px" };
    const assetCode = { fontSize: "12px", fontWeight: 800 };
    const assetName = { fontSize: "13px", fontWeight: 600 };
    const assetMeta = { fontSize: "12px", color: "#666" };
    const smallPill = { padding: "3px 10px", borderRadius: "14px", background: "rgba(0,0,0,0.06)", fontSize: "11px" };

   
	const documents = React.useMemo(function () {
	    return props.documents || [
	        {
	            id: 1,
	            title: "Installation Manual",
	            type: "PDF",
	            date: "12 Feb 2022"
	        },
	        {
	            id: 2,
	            title: "Electrical Test Certificate",
	            type: "PDF",
	            date: "14 Mar 2024"
	        },
	        {
	            id: 3,
	            title: "Maintenance Report",
	            type: "DOC",
	            date: "12 Feb 2026"
	        },
	        {
	            id: 4,
	            title: "As-Built Drawing",
	            type: "DWG",
	            date: "01 Jun 2021"
	        }
	    ];
	}, [props.documents]);
	
	
	const documentRow = {
	    display: "flex",
	    justifyContent: "space-between",
	    alignItems: "center",
	    padding: "12px 14px",
	    borderRadius: "12px",
	    background: "linear-gradient(180deg, #ffffff, #f7f8fa)",
	    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
	    cursor: "pointer",
	    transition: "all 0.18s ease"
	};

	const documentTitle = {
	    fontSize: "13px",
	    fontWeight: 600
	};

	const documentMeta = {
	    fontSize: "11px",
	    color: "#666"
	};

	const documentType = {
	    fontSize: "11px",
	    fontWeight: 700,
	    padding: "4px 10px",
	    borderRadius: "10px",
	    background: "rgba(0,0,0,0.06)"
	};
	
	 /* =========================
       RENDER
    ========================== */

    return (
        <div style={container}>

           

		<div style={{ ...kpiRow, gridTemplateColumns: "repeat(3, 1fr)" }}>
		    {[
		        { label: "Uptime", value: formatPercent(kpis.uptimePercent), sub: "30 days" },
		        { label: "Downtime", value: kpis.downtimeHours, sub: "hours" },
		        { label: "Outages", value: kpis.outages, sub: "events" }
		    ].map(function (k, i) {
		        return (
		            <div key={k.label} style={{ ...kpiCardBase, background: kpiColours[i + 1] }}>
		                <div style={kpiLabel}>{k.label}</div>
		                <div style={kpiValue}>{k.value}</div>
		                <div style={kpiSub}>{k.sub}</div>
		            </div>
		        );
		    })}
		</div>

            <div style={gridTwoCol}>

                <div style={sectionCard}>
                    <div style={sectionTitle}>Outages</div>
                    <AssetChartOutages/>

                    <div style={{ marginTop: "12px" }}>
                        <div style={sectionTitle}>Timeline</div>
                        <AssetChartAvailability/>
                    </div>
                </div>

				<div style={rightPanel}>
				    <div style={sectionTitle}>Documents</div>

				    <input
				        style={searchBox}
				        placeholder="Search documents"
				    />

				    <div style={{ height: "14px" }} />

				    <div style={list}>
				        {documents.map(function (d) {
				            return (
				                <div key={d.id} style={documentRow}>
				                    <div>
				                        <div style={documentTitle}>{d.title}</div>
				                        <div style={documentMeta}>{d.date}</div>
				                    </div>

				                    <div style={documentType}>{d.type}</div>
				                </div>
				            );
				        })}
				    </div>
				</div>

            </div>

        </div>
    );
}